import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Form, Row, Col } from 'react-bootstrap';
import { apiRequests } from '../../shared/api/apiRequests';
import { toast } from 'react-toastify';
import { Availability } from '../../entities/Availability';
import { SEOMetatagsForm } from '../../shared/ui/SEOMetatagsForm';
import { CreateContent } from '../../features/CreateContent';
import { UploadPicture } from '../../features/UploadPicture';
import { Guid } from 'js-guid';
import { CONTENT_ITEM_KEYS } from '../../features/CreateContent/constants';
import { FormLayout, FormSection, FormField, FormLoading } from '../../shared/ui/FormLayout';

const EditInvestProject = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [mainImage, setMainImage] = useState(null);
  const [presentation, setPresentation] = useState(null);
  const [currentImageURL, setCurrentImageURL] = useState('');
  const [currentPresentationURL, setCurrentPresentationURL] = useState('');
  
  const [formData, setFormData] = useState({
    id: 0,
    title: '',
    description: '',
    beforeBlocksTitle: '',
    block1Header: '',
    block1Text: '',
    block2Header: '',
    block2Text: '',
    block3Header: '',
    block3Text: '',
    content: {
      items: [],
    },
    availability: {
      availableInAllCountries: true,
      allowedCountriesIds: [],
      disallowedCountriesIds: [],
    },
    seoMetatags: {
      title: null,
      description: null,
      keywords: null,
    },
  });

  useEffect(() => {
    loadProject();
  }, [id]);

  const loadProject = async () => {
    try {
      setLoadingData(true);
      const response = await apiRequests.investProjects.getById(id);
      
      setFormData({
        id: response.id,
        title: response.title || '',
        description: response.description || '',
        beforeBlocksTitle: response.beforeBlocksTitle || '',
        block1Header: response.block1Header || '',
        block1Text: response.block1Text || '',
        block2Header: response.block2Header || '',
        block2Text: response.block2Text || '',
        block3Header: response.block3Header || '',
        block3Text: response.block3Text || '',
        content: response.content || { items: [] },
        availability: response.availability || {
          availableInAllCountries: true,
          allowedCountriesIds: [],
          disallowedCountriesIds: [],
        },
        seoMetatags: response.seoMetatags || {
          title: null,
          description: null,
          keywords: null,
        },
      });

      setCurrentImageURL(response.imageURL || '');
      setCurrentPresentationURL(response.presentationFileURL || '');
    } catch (error) {
      console.error('Error loading project:', error);
      toast.error('Ошибка загрузки проекта');
      navigate('/invest-projects');
    } finally {
      setLoadingData(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Валидация
    if (!formData.title.trim()) {
      toast.error('Введите название проекта');
      return;
    }
    if (!formData.description.trim()) {
      toast.error('Введите описание проекта');
      return;
    }

    setLoading(true);

    try {
      const body = new FormData();
      
      // Главное изображение (если изменяется)
      if (mainImage) {
        body.append('mainImg', mainImage, mainImage.name || 'image.jpg');
      }
      
      // Презентация (если изменяется)
      if (presentation) {
        body.append('presentation', presentation, presentation.name);
      }

      // Обработка Content Items
      const bodyItems = formData.content.items.map((i) => {
        switch (i.discriminator) {
          case 'TextContentItem':
            return i;
          case 'ImageSliderContentItem':
            const items = i.images.map((z) => {
              if (z.imgPath && typeof z.imgPath === 'object') {
                const guid = Guid.newGuid().toString();
                body.append(guid, z.imgPath);
                return { imgPath: '', guid, discriminator: 'ImageContentItem' };
              }
              return z;
            });
            return { ...i, images: items };
          default:
            if (i[CONTENT_ITEM_KEYS[i.discriminator]] && typeof i[CONTENT_ITEM_KEYS[i.discriminator]] === 'object') {
              body.append(i.guid, i[CONTENT_ITEM_KEYS[i.discriminator]]);
              return { ...i, [CONTENT_ITEM_KEYS[i.discriminator]]: '' };
            }
            return i;
        }
      });

      const bodyModel = JSON.parse(JSON.stringify(formData));
      bodyModel.content.items = bodyItems;

      body.append('model', JSON.stringify(bodyModel));

      await apiRequests.investProjects.update(body);
      
      toast.success('Инвест проект обновлен');
      navigate('/invest-projects');
    } catch (error) {
      console.error('Error updating invest project:', error);
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.message || 
                          'Ошибка при обновлении проекта';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (loadingData) return <FormLoading />;

  return (
    <FormLayout
      onSubmit={handleSubmit}
      onBack={() => navigate('/invest-projects')}
      saving={loading}
      submitText="Сохранить"
      savingText="Сохранение..."
    >
      <FormSection title="Основная информация">
        <Row>
          <Col lg={8}>
            <FormField label="Название проекта" required>
              <Form.Control
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="Введите название проекта"
              />
            </FormField>

            <FormField label="Описание" required>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Введите описание проекта"
              />
            </FormField>
          </Col>

          <Col lg={4}>
            <FormField label="Главное изображение">
              <UploadPicture
                onSelect={setMainImage}
                initialValue={currentImageURL}
                entityType="portfolio"
              />
            </FormField>

            <FormField label="Презентация">
              {currentPresentationURL && !presentation && (
                <div className="mb-2">
                  <a 
                    href={currentPresentationURL} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="btn btn-sm btn-outline-primary"
                  >
                    Текущая презентация
                  </a>
                </div>
              )}
              <Form.Control
                type="file"
                accept=".pdf,.ppt,.pptx,.doc,.docx"
                onChange={(e) => setPresentation(e.target.files[0])}
              />
              {presentation && (
                <small className="text-muted d-block mt-1">
                  Выбран: {presentation.name}
                </small>
              )}
            </FormField>
          </Col>
        </Row>
      </FormSection>

      <FormSection title="Блоки информации">
        <FormField label="Заголовок перед блоками">
          <Form.Control
            type="text"
            name="beforeBlocksTitle"
            value={formData.beforeBlocksTitle}
            onChange={handleChange}
            placeholder="Введите заголовок"
          />
        </FormField>

        <Row>
          <Col md={4}>
            <h6 className="fw-medium mb-3">Блок 1</h6>
            <FormField label="Заголовок">
              <Form.Control
                type="text"
                name="block1Header"
                value={formData.block1Header}
                onChange={handleChange}
              />
            </FormField>
            <FormField label="Текст">
              <Form.Control
                as="textarea"
                rows={2}
                name="block1Text"
                value={formData.block1Text}
                onChange={handleChange}
              />
            </FormField>
          </Col>
          <Col md={4}>
            <h6 className="fw-medium mb-3">Блок 2</h6>
            <FormField label="Заголовок">
              <Form.Control
                type="text"
                name="block2Header"
                value={formData.block2Header}
                onChange={handleChange}
              />
            </FormField>
            <FormField label="Текст">
              <Form.Control
                as="textarea"
                rows={2}
                name="block2Text"
                value={formData.block2Text}
                onChange={handleChange}
              />
            </FormField>
          </Col>
          <Col md={4}>
            <h6 className="fw-medium mb-3">Блок 3</h6>
            <FormField label="Заголовок">
              <Form.Control
                type="text"
                name="block3Header"
                value={formData.block3Header}
                onChange={handleChange}
              />
            </FormField>
            <FormField label="Текст">
              <Form.Control
                as="textarea"
                rows={2}
                name="block3Text"
                value={formData.block3Text}
                onChange={handleChange}
              />
            </FormField>
          </Col>
        </Row>
      </FormSection>

      <FormSection title="Контент страницы">
        <CreateContent
          form={formData}
          setForm={setFormData}
        />
      </FormSection>

      <FormSection title="Доступность">
        <Availability
          onSelect={(val) =>
            setFormData((prev) => ({
              ...prev,
              availability: val,
            }))
          }
          value={formData.availability}
        />
      </FormSection>

      <FormSection title="SEO настройки">
        <SEOMetatagsForm
          seoMetatags={formData.seoMetatags}
          onChange={(updatedMetatags) =>
            setFormData((prev) => ({
              ...prev,
              seoMetatags: updatedMetatags,
            }))
          }
        />
      </FormSection>
    </FormLayout>
  );
};

export default EditInvestProject;
